import { Alert, Button, DialogTitle, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import CameraControls from 'camera-controls'
import { ApplicationTypes } from 'element-domain'
import { useCallback, useState } from 'react'
import { PerspectiveCamera } from 'three'
import { v4 } from 'uuid'
import { z } from 'zod'

import { useLogger, useMountGuard } from '@salsita-ai/platform-project-sdk/react'
import { Dialog, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { SCENE_BASE_LAYER, SCENE_TABLETOP_LAYER } from '../../../scene/AppEnvironment'
import { commitPopOrNavigateToSummary } from '../../../systems/commit-pop-or-summary'
import { configuratorInterface } from '../../../systems/configurator-interface'
import { FinalizeForm } from '../helper-components/FinalizeForm'
import { SubmitButton } from '../helper-components/SubmitButton'
import { QuoteSuccessDialogContent } from './dialog-content/QuoteSuccessDialogContent'

const FORM_ID = 'finalize-form'

const IMAGE_HEIGHT = 720
const IMAGE_WIDTH = 1080

const formSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  emailAddress: z.string().email(),
  // The phoneNumber regex now allows either an empty string or exactly 10 digits.
  phoneNumber: z.string().regex(/^$|^[0-9]{10}$/, { message: 'Phone number must be empty or exactly 10 digits.' }),
  message: z.string().trim().min(1, { message: 'Message is required' }),
})

export default function FinalizeQuoteDialog() {
  const [finalizationUuid, setFinalizationUuid] = useState<string | null>(null)

  const frontendData = configuratorInterface.useFrontendData()

  const { navigationSystem, storeSystem, configurationId } = configuratorInterface.useDomain()
  const { imageRenderingSystem } = configuratorInterface.useConfigurator()
  const committedState = configuratorInterface.useCommittedState()
  const { configurationPersistSystem } = configuratorInterface.useDomainApplication()
  const apiClient = configuratorInterface.useAuthorizedClient()
  const isMounted = useMountGuard()
  // const logger = useLogger()

  const generateImages = useCallback(
    async (finalizationUuid: string) => {
      await imageRenderingSystem.generateImages(finalizationUuid, [
        {
          name: 'clientGeneratedImage',
          width: IMAGE_WIDTH,
          height: IMAGE_HEIGHT,
          getCamera: async scene => {
            // Scene uses mm-scale units; far=10k clips easily after fit + dolly (blank export).
            const camera = new PerspectiveCamera(45, IMAGE_WIDTH / IMAGE_HEIGHT, 10, 250000)
            camera.layers.enable(SCENE_TABLETOP_LAYER)
            camera.layers.enable(SCENE_BASE_LAYER)
            const controls = new CameraControls(camera)

            const mesh = scene.getObjectByName(String(committedState.rootEntityId))

            if (mesh == null) {
              throw new Error('Root entity not found!')
            }

            await controls.fitToSphere(mesh, false)
            await controls.rotateTo(-0.5, 1.25, false)

            // Move camera farther away from the object
            await controls.dollyTo(controls.distance + 1000, false) // Increase distance by 1000 units

            // To mock the "animations"
            controls.update(1000000)

            return camera
          },
        },
      ])
    },
    [imageRenderingSystem, committedState.rootEntityId]
  )

  const finalize = useCallback(
    async (data: unknown) => {
      const finalizationUuid = v4()

      const parsedData = formSchema.parse(data)

      await Promise.all([configurationPersistSystem.save(), generateImages(finalizationUuid)]) // TODO: what is configurationPersistSystem

      await apiClient.finalize(
        configurationId,
        finalizationUuid,
        {
          emailAddress: parsedData.emailAddress,
          firstName: parsedData.firstName,
          lastName: parsedData.lastName,
          phoneNumber: parsedData.phoneNumber,
          message: parsedData.message,
          submissionDate: new Date().toISOString(),
          addToCart: false,
        },
        undefined,
        frontendData
      )

      window.dataLayer = window.dataLayer ?? []
      window.dataLayer.push({
        event: 'configurator_request_quote',
        formID: FORM_ID,
        firstName: parsedData.firstName,
        lastName: parsedData.lastName,
        emailAddress: parsedData.emailAddress,
        phoneNumber: parsedData.phoneNumber,
        message: parsedData.message,
        source: 'salsita-configurator',
      })

      if (isMounted()) {
        setFinalizationUuid(finalizationUuid)
      }
    },
    [apiClient, configurationPersistSystem, generateImages, configurationId, isMounted, frontendData]
  )

  const handleBack = useCallback(() => {
    commitPopOrNavigateToSummary(navigationSystem, storeSystem, ApplicationTypes.NavigationState.Summary)
  }, [navigationSystem, storeSystem])

  const logger = useLogger()

  const { mutate, isPending, isError, isSuccess } = useMutation({
    mutationKey: ['finalize'],
    mutationFn: finalize,
    onError(error) {
      logger.error('Finalization failed:', {}, error)
    },
  })

  return (
    <Dialog.Root variant="static" sx={{ width: '70%', maxWidth: isSuccess ? '500px' : '700px' }}>
      {isSuccess && finalizationUuid && <QuoteSuccessDialogContent finalizationUuid={finalizationUuid} />}
      {!isSuccess && (
        <Dialog.ContentLayout
          header={<DialogTitle sx={{ px: 2 }}>Request a quote</DialogTitle>}
          controlsPosition="end"
          body={
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Send us your contact details and a short message about your request. We will email you a quote PDF, a
                preview image of your table, and links to review or share your configuration.
              </Typography>
              <FinalizeForm onSubmit={mutate} formId={FORM_ID} />
              {isError && (
                <Alert severity="error">
                  <b>Your quote request could not be sent.</b>
                  <br />
                  Please try again later. If the issue persists, contact website support.
                </Alert>
              )}
            </>
          }
          controls={
            <>
              <Button variant="text" onClick={handleBack} startIcon={<PhxIcon name="ArrowLeft" />}>
                Back to Summary
              </Button>
              <SubmitButton isPending={isPending} title="Submit quote request" formId={FORM_ID} />
            </>
          }
        />
      )}
    </Dialog.Root>
  )
}
